import { Box } from '@mui/material';
import { TeamMembersTable } from './TeamMembersTable';
import { TeamMembersTableHeader } from './TeamMembersTableHeader';
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import debounce from 'lodash/debounce';
import { TeamMembershipType } from 'types';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'redux/hooks';
import { setFilters } from 'actionCreators';
import { selectPaginatedTeamMembers, selectTeamMemberTableFilter } from 'selectors';
import { searchTeamMembers } from 'ProfileModule/thunks';
import { conditionallyRenderElement } from 'utils';
import { isMosaicSupportAccount } from './helpers/mosaicSupportAccount';
import { buildTeamMembersSearchParams, resolveSortField } from './helpers/teamMembersSearchParams';
import { useGrowthBookFlag } from 'hooks/useGrowthBookFlag';

const SEARCH_DEBOUNCE_MS = 300;

export const TeamMembersTableContainer: React.FC<{
  teamId: number;
  /** Whether the Rails session/realm context matches the URL tenant yet. */
  isContextReady?: boolean;
  /** Tenant name for API context - ensures actions target the correct tenant */
  tenant?: string;
  /** Realm ID for API context - ensures actions target the correct realm */
  realmId?: string;
}> = ({ teamId, tenant, realmId, isContextReady = true }) => {
  const dispatch = useAppDispatch();

  // The teamMemberTable filter is the single source of truth for pagination +
  // sort. `useRequestSort` (in TeamMembersTable) already resets offset/page to 0
  // when the sort column/direction changes.
  const filter = useSelector(selectTeamMemberTableFilter);
  const { members, totalCount, isFetching, error } = useSelector(selectPaginatedTeamMembers);

  // Gates both halves of the Mosaic Support work together: the server-side pin
  // and reading `is_csm_account` instead of the name+email heuristic. Off means
  // exactly the pre-BE-12303 behavior, which is what makes the two services
  // safe to deploy in either order.
  //
  // The flag decides a query param, so fetching before flag values load would
  // fire a second search (and clear the selection) the moment they arrive.
  // Readiness is true on load failure and timeout too, so this can't hang.
  const { isOn: isCsmSupportAccountFlagOn, isReady: areFeatureFlagsReady } = useGrowthBookFlag(
    'galaxyCsmSupportAccountFlag',
  );

  const [selectedMemberIds, setSelectedMemberIdsSet] = useState<Set<number>>(new Set());
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [hideArchivedMembers, setHideArchivedMembers] = useState(true);

  const { id: filterId, limit, offset, orderBy, orderDirection } = filter;
  const pageNumber = filter.page ?? 0;

  // Pagination/sort live in the persisted Redux filter, so on entering this tab
  // (or switching team/realm) snap back to the first page; otherwise a page
  // index carried over from another team could leave us out of range. Sort is
  // intentionally left sticky.
  const scopeKey = `${teamId}|${tenant ?? ''}|${realmId ?? ''}`;
  const prevScopeRef = useRef<string | null>(null);

  // Debounced commit of the search box. Resetting to the first page and the
  // search-text commit happen in one callback so React batches them into a
  // single fetch.
  const commitSearch = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearch(value);
        dispatch(setFilters({ filterId, offset: 0, page: 0 }));
      }, SEARCH_DEBOUNCE_MS),
    [dispatch, filterId],
  );
  useEffect(() => () => commitSearch.cancel(), [commitSearch]);

  const searchMember = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      setSearchInput(value);
      commitSearch(value);
    },
    [commitSearch],
  );

  const onToggleHideArchivedMembers = useCallback(() => {
    setHideArchivedMembers((prev) => !prev);
    dispatch(setFilters({ filterId, offset: 0, page: 0 }));
  }, [dispatch, filterId]);

  const onPageChange = useCallback(
    (_event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, page: number) => {
      dispatch(setFilters({ filterId, page, offset: page * limit }));
    },
    [dispatch, filterId, limit],
  );

  // Fetch the current page whenever a query input (page / sort / search /
  // filter / pin) changes. Selection is per-page, so it is cleared here on each
  // such change — the pin flag included, since pinning reorders the page and a
  // kept selection could then act on a row that is no longer visible. In-place
  // row mutations (member update / bulk activate) don't touch these deps, so
  // they preserve the selection for follow-up bulk actions.
  useEffect(() => {
    if (!teamId || !isContextReady || !areFeatureFlagsReady) return;

    // On first entry / team switch, reset to the first page. The resulting
    // offset change re-triggers this effect, which then fetches.
    const scopeChanged = prevScopeRef.current !== scopeKey;
    prevScopeRef.current = scopeKey;
    if (scopeChanged && offset !== 0) {
      // Drop any selection carried over from the previous team up front, so it
      // can't linger during the render before the offset-reset re-runs this
      // effect and fetches.
      setSelectedMemberIdsSet(new Set());
      dispatch(setFilters({ filterId, offset: 0, page: 0 }));
      return;
    }

    dispatch(
      searchTeamMembers({
        teamId,
        tenant,
        realmId,
        limit,
        offset,
        sort: resolveSortField(orderBy),
        sort_direction: orderDirection === 'desc' ? 'desc' : 'asc',
        ...(hideArchivedMembers ? {} : { include_discarded: 'true' }),
        ...(isCsmSupportAccountFlagOn ? { pin_csm: 'true' as const } : {}),
        ...buildTeamMembersSearchParams(debouncedSearch),
      }),
    );
    setSelectedMemberIdsSet(new Set());
  }, [
    dispatch,
    teamId,
    tenant,
    realmId,
    scopeKey,
    filterId,
    isContextReady,
    limit,
    offset,
    orderBy,
    orderDirection,
    debouncedSearch,
    hideArchivedMembers,
    isCsmSupportAccountFlagOn,
    areFeatureFlagsReady,
  ]);

  const selectedMemberIdsArray = Array.from(selectedMemberIds);

  const selectTeamMember = useCallback(
    ({ accountId }: { accountId: TeamMembershipType['account_id'] }) => {
      setSelectedMemberIdsSet((prev) => {
        const next = new Set(prev);
        if (next.has(accountId)) {
          next.delete(accountId);
        } else {
          next.add(accountId);
        }
        return next;
      });
    },
    [],
  );

  const clearAllSelectedMembers = useCallback(() => {
    setSelectedMemberIdsSet(new Set());
  }, []);

  // Members the header "select all" checkbox may toggle. Mosaic Support
  // memberships are excluded so bulk operations never target the system-owned
  // support account. Server-side pagination means this scopes to the current
  // page only.
  const selectableMemberIds = useMemo(
    () =>
      members
        .filter(
          (member) =>
            !isMosaicSupportAccount(member, { useCsmAccountField: isCsmSupportAccountFlagOn }),
        )
        .map((member) => member.account_id),
    [members, isCsmSupportAccountFlagOn],
  );

  const selectAllSelectableMembers = useCallback(() => {
    setSelectedMemberIdsSet((prev) => {
      const next = new Set(prev);
      selectableMemberIds.forEach((id) => next.add(id));
      return next;
    });
  }, [selectableMemberIds]);

  const clearAllSelectableMembers = useCallback(() => {
    setSelectedMemberIdsSet((prev) => {
      if (prev.size === 0) return prev;
      const next = new Set(prev);
      selectableMemberIds.forEach((id) => next.delete(id));
      return next;
    });
  }, [selectableMemberIds]);

  const paginationProps = useMemo(
    () => ({
      onPageChange,
      count: totalCount,
      page: pageNumber,
      rowsPerPage: limit,
      rowsPerPageOptions: [] as number[],
    }),
    [onPageChange, totalCount, pageNumber, limit],
  );

  // Only block the whole tab on the first load; keep the current page rendered
  // while paginating/sorting/searching to avoid a jarring full-screen reload.
  // The effect's two gates count as loading: nothing is fetching while they are
  // closed, so without them the tab would show an empty table rather than the
  // loader. Neither gate can stick closed — both resolve on failure too.
  const isInitialLoading =
    (!isContextReady || !areFeatureFlagsReady || isFetching) && members.length === 0;

  return conditionallyRenderElement({
    isLoading: isInitialLoading,
    error: members.length === 0 ? error?.message : undefined,
    content: (
      <Box
        sx={{
          padding: 4,
        }}
      >
        <TeamMembersTableHeader
          teamId={teamId}
          selectedAccountIds={selectedMemberIdsArray}
          clearAll={clearAllSelectedMembers}
          searchedMemberName={searchInput}
          searchMember={searchMember}
          hideArchivedMembers={hideArchivedMembers}
          onToggleHideArchivedMembers={onToggleHideArchivedMembers}
          tenant={tenant}
          realmId={realmId}
        />
        <TeamMembersTable
          selectTeamMember={selectTeamMember}
          selectedMemberIds={selectedMemberIds}
          selectableMemberIds={selectableMemberIds}
          selectAllSelectableMembers={selectAllSelectableMembers}
          clearAllSelectableMembers={clearAllSelectableMembers}
          teamMemberships={members}
          paginationProps={paginationProps}
          teamId={teamId}
        />
      </Box>
    ),
  });
};
